import {
  APPLE_PAY_PAYMENT_TYPE,
  AZUPAY_PAY_ID_PAYMENT_TYPE,
  GOOGLE_PAY_PAYMENT_TYPE,
  PAYLATER_PAYMENT_TYPE,
  PAYPAL_PAYMENT_TYPE,
  STRIPE_CUSTOM_PAYTO_TYPE,
  STRIPE_PAYMENT_ELEMENT_CARD_TYPE,
  type PaymentMethod,
} from 'constants/payment'

/**
 * Low cost payment methods earn this many times the base Société points.
 */
export const LOYALTY_PAYMENT_METHOD_EARN_MULTIPLIER = 2

/**
 * Checkout payment rows that show a pill for earning the multiplier. Hardcoded to match svc-lux-loyalty's low cost
 * list (LOYAL-2850) until it is served by an API. The earn figure itself always comes from svc-lux-loyalty.
 */
export const LOYALTY_MULTIPLIER_PAYMENT_METHODS: ReadonlySet<PaymentMethod> = new Set<PaymentMethod>([
  PAYPAL_PAYMENT_TYPE,
  PAYLATER_PAYMENT_TYPE,
  STRIPE_CUSTOM_PAYTO_TYPE,
  AZUPAY_PAY_ID_PAYMENT_TYPE,
])

/**
 * Cards earn the multiplier when they are Amex (any funding) or debit or prepaid (any brand).
 * Older saved cards name the Amex brand in full, so both spellings are listed.
 */
export const LOYALTY_MULTIPLIER_CARD_BRANDS: ReadonlySet<string> = new Set(['amex', 'american express'])
export const LOYALTY_MULTIPLIER_CARD_FUNDINGS: ReadonlySet<string> = new Set(['debit', 'prepaid'])

/**
 * `ibsEarnKey` of the svc-lux-loyalty rule that awards the payment-method bonus, as returned in
 * `customRulesBreakdown`. Checkout only reads it to tell that bonus apart from campaign bonuses; the
 * bonus itself is requested by sending `paymentMethods`, never an earn key.
 */
export const LOYALTY_PAYMENT_METHOD_BONUS_IBS_EARN_KEY = 'paymentMethodCost=low'

export const LOYALTY_EARN_CARD_PAYMENT_TYPE = 'card'
export const LOYALTY_EARN_CREDIT_PAYMENT_TYPE = 'credits'
export const LOYALTY_EARN_GIFT_CARD_PAYMENT_TYPE = 'gift_card'
export const LOYALTY_EARN_APPLE_PAY_PAYMENT_TYPE = 'apple_pay'
export const LOYALTY_EARN_GOOGLE_PAY_PAYMENT_TYPE = 'google_pay'
export const LOYALTY_EARN_LINK_PAYMENT_TYPE = 'link'
export const LOYALTY_EARN_POINTS_PAYMENT_TYPE = 'le_points'
export const LOYALTY_EARN_VELOCITY_PAYMENT_TYPE = 'velocity'

/**
 * calculate-earn expects its own payment type names (Jun, 2026-10-12), which differ from checkout's for
 * some methods. Checkout types not listed here are sent unchanged; unknown types earn the base rate.
 * Pay in 4 earns 2x like PayPal (LOYAL-2884), so it is sent as `paypal` until its own type is confirmed.
 */
export const LOYALTY_EARN_PAYMENT_TYPES: Utils.PartialRecord<PaymentMethod, string> = {
  [STRIPE_PAYMENT_ELEMENT_CARD_TYPE]: LOYALTY_EARN_CARD_PAYMENT_TYPE,
  [STRIPE_CUSTOM_PAYTO_TYPE]: 'payto',
  [PAYLATER_PAYMENT_TYPE]: 'paypal',
  [APPLE_PAY_PAYMENT_TYPE]: LOYALTY_EARN_APPLE_PAY_PAYMENT_TYPE,
  [GOOGLE_PAY_PAYMENT_TYPE]: LOYALTY_EARN_GOOGLE_PAY_PAYMENT_TYPE,
}

/**
 * Stripe reports cards paid through a wallet, including Link inside the card row, with a wallet type.
 * Wallets always earn the base rate, whatever card is behind them, so they are sent as their own types.
 */
export const LOYALTY_EARN_WALLET_PAYMENT_TYPES: Record<string, string> = {
  apple_pay: LOYALTY_EARN_APPLE_PAY_PAYMENT_TYPE,
  google_pay: LOYALTY_EARN_GOOGLE_PAY_PAYMENT_TYPE,
  link: LOYALTY_EARN_LINK_PAYMENT_TYPE,
}
